import moment from 'moment-timezone';
import { createSelector } from 'reselect';
import { isPresent } from 'utils/isPresent';
import CustomersApi from '../api/CustomersApi';
import { EmptyObject } from '../../../Resources/EmptyDefaults';
import { isBlank } from 'utils/isBlank';

export const CUSTOMER_NAMESPACE = 'customerDetail';
export const CUSTOMER_CONTACT_INFO_KEY = 'contact_info';
export const CUSTOMER_CARD_ON_FILE_KEY = 'card_on_file';
export const CUSTOMER_PAYMENT_STATS = 'payment_stats';
export const customer = (state) => state.customerDetail || EmptyObject;

export const getCustomerPaymentStats = createSelector(
  customer,
  (customer) => customer.payment_stats || EmptyObject,
);

export const getOutstandingBalance = createSelector(
  getCustomerPaymentStats,
  (stats) => stats.outstanding_balance || 0,
);

export const getTotalOutstandingInvoices = createSelector(
  getCustomerPaymentStats,
  ({ outstanding_invoices_count }) => outstanding_invoices_count || 0,
);

export const getCustomerLifetimeValue = createSelector(
  getCustomerPaymentStats,
  (stats) => stats.life_time_value || 0,
);

export const getCustomerContactInfo = createSelector(
  customer,
  (customer) => customer.contact_info || EmptyObject,
);

export const getCustomerAddresses = createSelector(
  customer,
  (customer) => customer.addresses || EmptyObject,
);

export const getCustomerDisplayName = createSelector(
  getCustomerContactInfo,
  (contactInfo) => contactInfo.display_name || '',
);

export const getCustomerId = createSelector(
  getCustomerContactInfo,
  (contactInfo) => contactInfo.id || EmptyObject,
);

export const getCustomerUuid = createSelector(
  getCustomerContactInfo,
  (contactInfo) => contactInfo.uuid || '',
);

export const getParentCustomer = createSelector(
  getCustomerContactInfo,
  (contactInfo) => contactInfo.parent_customer || EmptyObject,
);

export const getChildCustomers = createSelector(
  getCustomerContactInfo,
  (contactInfo) => contactInfo.child_customers || EmptyObject,
);

export const getCreatedAtDate = createSelector(
  getCustomerContactInfo,
  (contactInfo) => {
    if (isBlank(contactInfo.created_at)) {
      return null;
    }
    return moment(contactInfo.created_at.time).tz(contactInfo.created_at.time_zone).format('MM/DD/YYYY');
  },
);

export const getEmailAddresses = createSelector(
  getCustomerContactInfo,
  (contactInfo) => {
    const emailIds = [contactInfo.email];
    if (isPresent(contactInfo.additional_emails)) {
      contactInfo.additional_emails.map((value) => {
        emailIds.push(value.email);
      });
    }
    return emailIds;
  },
);

export const getBatchableEmails = createSelector(
  [getCustomerContactInfo, getParentCustomer],
  (contact, parent) => {
    if (isPresent(parent.email)) return parent.email;

    let emails = [contact.email];

    if (isPresent(contact.additional_emails)) {
      const additionalEmails = contact.additional_emails.map((additionalEmail) => additionalEmail.email);

      emails = [...emails, ...additionalEmails];
    }

    return emails.filter((email) => isPresent(email)).join(', ');
  },
);

export const getLastServiceAtDate = createSelector(
  getCustomerContactInfo,
  (profile) => {
    if (isBlank(profile.last_service_at)) {
      return null;
    }
    return moment(profile.last_service_at.time).tz(profile.last_service_at.time_zone).format('MM/DD/YYYY');
  },
);

export const isNotificationEnabled = createSelector(
  getCustomerContactInfo,
  (contact) => contact.notifications_enabled,
);

export const hasQuickbooksLink = createSelector(
  customer,
  (customer) => customer.has_quickbooks_link,
);

export const fetchCustomer = {
  fetchingApiFunc: CustomersApi.get,
  selector: customer,
  paramsFromProps: (props) => (
    [isPresent(props.match) ? props.match.params.id : props.customer_id]
  ),
  dataNamespace: CUSTOMER_NAMESPACE,
};

export const getCustomerPhoneNumber = createSelector(
  customer,
  (customer) => {
    if (customer.contact_info?.mobile_number) {
      return `+1${customer.contact_info.mobile_number.replace(/[^0-9]/g, '')}`;
    }
    if (customer.contact_info?.home_number) {
      return `+1${customer.contact_info.home_number.replace(/[^0-9]/g, '')}`
    }
    if (customer.contact_info?.work_number) {
      return `+1${customer.contact_info.work_number.replace(/[^0-9]/g, '')}`
    }
    if(customer.additional_phone_numbers?.[0]) {
      return `+1${customer.additional_phone_numbers[0].replace(/[^0-9]/g, '')}`
    }
    return null
  },
);
